import { EventTypeConfig, IRegionInterviewConfig } from '../region-config/index.js';
import { CoreStatusExtended } from '../offer/enums.js';
import { Either } from '../shared-types.js';

export type BulkActionCommon = Either<{ filters: IGetStudentsParams }, { studentIDs: string[] }>;
export type AssignStageBulkActionAPIPayload = { data: CoreStatusExtended } & BulkActionCommon;
export type AssignImmigrationBulkActionAPIPayload = { data: string } & BulkActionCommon;
export type AddTagsBulkActionAPIPayload = { data: string[] } & BulkActionCommon;
export type RemoveTagsBulkActionAPIPayload = { data: string[] } & BulkActionCommon;
export type RemoveRecordedInterviewBulkActionAPIPayload = { data: string } & BulkActionCommon;
export type RemoveScheduledInterviewBulkActionAPIPayload = { data: string } & BulkActionCommon;
export type AddPartnersBulkActionAPIPayload = { data: string[] } & BulkActionCommon;
export type RemovePartnersBulkActionAPIPayload = { data: string[] } & BulkActionCommon;
export type RequestRecordedInterviewBulkActionAPIPayload = { data: IRegionInterviewConfig[] } & BulkActionCommon;
export type RequestScheduledInterviewBulkActionAPIPayload = { data: EventTypeConfig[] } & BulkActionCommon;

export type BulkActionsAPIPayload =
  | AssignStageBulkActionAPIPayload
  | AssignImmigrationBulkActionAPIPayload
  | AddTagsBulkActionAPIPayload
  | RemoveTagsBulkActionAPIPayload
  | RemoveRecordedInterviewBulkActionAPIPayload
  | RemoveScheduledInterviewBulkActionAPIPayload
  | AddPartnersBulkActionAPIPayload
  | RemovePartnersBulkActionAPIPayload
  | RequestRecordedInterviewBulkActionAPIPayload
  | RequestScheduledInterviewBulkActionAPIPayload;

export type IGetStudentsParams = IFilterParams & AdditionalStudentsFilterParams;
export type IGetFiltersParams = IFilterParams & AdditionalFilterParams;

export enum ApiFilterKeys {
  UNI = 'uni',
  STUDENT_NO = 'studentNo',
  GIVEN_NAME = 'givenName',
  SURNAME = 'surname',
  COURSE = 'course',
  FACULTY = 'faculty',
  NATIONALITY = 'nationality',
  BIRTH_COUNTRY = 'birthCountry',
  COUNTRY = 'country',
  APPLICATION_COUNTRY = 'applicationCountry',
  ACTIVITY_STATUS = 'activityStatus',
  NOT_STARTED = 'notStarted',
  IDLE = 'idle',
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  FUNDS_MATURITY = 'fundsMaturity',
  MARKET = 'market',
  REGION = 'region',
  VISA_STATUS = 'visaStatus',
  CONFIDENCE = 'confidence',
  AGENT = 'agent',
  CONDITIONAL_STATUS = 'conditionalStatus',
  COURSE_CONDITIONAL_STATUS = 'courseConditionalStatus',
  EVIDENCE_REQUIREMENTS = 'evidenceRequirements',
  CUSTOM_REGION = 'customRegion',
  TAG = 'tag',
  PROGRAMMABLE_TAG = 'programmableTag',
  SERVICES = 'services',
  IMMIGRATION_PROVIDERS = 'immigrationProviders',
  INTERVIEW = 'interview',
  RECORDED_INTERVIEW = 'recordedInterview',
  IDLE_FROM = 'idleFrom',
  IDLE_TO = 'idleTo',
  FEE_STATUS = 'feeStatus',
  OFFER_STATUS = 'offerStatus',
  SHARE_CODE = 'shareCode',
}

export interface IFilterParams {
  [ApiFilterKeys.UNI]?: Array<string>;
  [ApiFilterKeys.STUDENT_NO]?: Array<string>;
  [ApiFilterKeys.GIVEN_NAME]?: Array<string>;
  [ApiFilterKeys.SURNAME]?: Array<string>;
  [ApiFilterKeys.COURSE]?: Array<string>;
  [ApiFilterKeys.FACULTY]?: Array<string>;
  [ApiFilterKeys.NATIONALITY]?: Array<string>;
  [ApiFilterKeys.COUNTRY]?: Array<string>;
  [ApiFilterKeys.APPLICATION_COUNTRY]?: Array<string>;
  [ApiFilterKeys.ACTIVITY_STATUS]?: Array<string>;
  [ApiFilterKeys.NOT_STARTED]?: Array<string>;
  [ApiFilterKeys.IDLE]?: Array<string>;
  [ApiFilterKeys.COMPLETE]?: Array<string>;
  [ApiFilterKeys.INCOMPLETE]?: Array<string>;
  [ApiFilterKeys.FUNDS_MATURITY]?: Array<string>;
  [ApiFilterKeys.MARKET]?: Array<string>;
  [ApiFilterKeys.REGION]?: Array<string>;
  [ApiFilterKeys.VISA_STATUS]?: Array<string>;
  [ApiFilterKeys.CONFIDENCE]?: Array<string>;
  [ApiFilterKeys.AGENT]?: Array<string>;
  [ApiFilterKeys.CONDITIONAL_STATUS]?: Array<string>;
  [ApiFilterKeys.COURSE_CONDITIONAL_STATUS]?: Array<string>;
  [ApiFilterKeys.EVIDENCE_REQUIREMENTS]?: Array<string>;
  [ApiFilterKeys.CUSTOM_REGION]?: Array<string>;
  [ApiFilterKeys.TAG]?: Array<string>;
  [ApiFilterKeys.PROGRAMMABLE_TAG]?: Array<string>;
  [ApiFilterKeys.SERVICES]?: Array<string>;
  [ApiFilterKeys.IMMIGRATION_PROVIDERS]?: Array<string>;
  [ApiFilterKeys.INTERVIEW]?: Array<string>;
  [ApiFilterKeys.RECORDED_INTERVIEW]?: Array<string>;
  [ApiFilterKeys.IDLE_FROM]?: Array<number>;
  [ApiFilterKeys.IDLE_TO]?: Array<number>;
  [ApiFilterKeys.SHARE_CODE]?: Array<string>;
}

export enum AdditionalStudentApiFilterKeys {
  ABORT_CONTROLLER = 'abortController',
  ID_TOKEN = 'idToken',
  STAGE = 'stage',
  PAGE = 'page',
  UNI = 'uni',
  PATHWAY = 'pathway',
  INTAKE_ID = 'intakeId',
  PATHWAY_VISIBILITY = 'pathwayVisibility',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SORT_COL = 'sortCol',
  SORT_DIR = 'sortDir',
  SEARCH = 'search',
  UNREAD_MESSAGES = 'unreadMessages',
}

export interface AdditionalStudentsFilterParams {
  [AdditionalStudentApiFilterKeys.ABORT_CONTROLLER]?: AbortController | false;
  [AdditionalStudentApiFilterKeys.ID_TOKEN]: unknown;
  [AdditionalStudentApiFilterKeys.STAGE]: string;
  [AdditionalStudentApiFilterKeys.PAGE]?: number;
  [AdditionalStudentApiFilterKeys.UNI]?: string;
  [AdditionalStudentApiFilterKeys.PATHWAY]?: string;
  [AdditionalStudentApiFilterKeys.INTAKE_ID]?: string;
  [AdditionalStudentApiFilterKeys.PATHWAY_VISIBILITY]?: string;
  [AdditionalStudentApiFilterKeys.START_DATE]?: string;
  [AdditionalStudentApiFilterKeys.END_DATE]?: string;
  [AdditionalStudentApiFilterKeys.SORT_COL]?: string;
  [AdditionalStudentApiFilterKeys.SORT_DIR]?: string;
  [AdditionalStudentApiFilterKeys.SEARCH]?: string;
  [AdditionalStudentApiFilterKeys.UNREAD_MESSAGES]?: string;
}

export enum AdditionalApiFilterKeys {
  ABORT_CONTROLLER = 'abortController',
  ID_TOKEN = 'idToken',
  UNI = 'uni',
  PATHWAY = 'pathway',
  INTAKE_ID = 'intakeId',
  PATHWAY_VISIBILITY = 'pathwayVisibility',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SEARCH = 'search',
}

export interface AdditionalFilterParams {
  [AdditionalApiFilterKeys.ABORT_CONTROLLER]?: AbortController | false;
  [AdditionalApiFilterKeys.ID_TOKEN]: unknown;
  [AdditionalApiFilterKeys.UNI]?: string;
  [AdditionalApiFilterKeys.PATHWAY]?: string;
  [AdditionalApiFilterKeys.INTAKE_ID]?: string;
  [AdditionalApiFilterKeys.PATHWAY_VISIBILITY]?: string;
  [AdditionalApiFilterKeys.START_DATE]?: string;
  [AdditionalApiFilterKeys.END_DATE]?: string;
  [AdditionalApiFilterKeys.SEARCH]?: string;
}
